import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, SearchX } from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import { AttachmentPreview } from '@/components/feedback/AttachmentPreview'
import { DiagnosticsPanel } from '@/components/feedback/DiagnosticsPanel'
import { AwaitingReplyBadge, FeedbackStatusBadge } from '@/components/feedback/FeedbackStatusBadge'
import { FeedbackThread } from '@/components/feedback/FeedbackThread'
import { FeedbackTimeline } from '@/components/feedback/FeedbackTimeline'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/hooks/useAuth'
import { useFeedbackReport } from '@/hooks/useFeedback'
import { ApiError } from '@/lib/api-error'
import { formatDateTime } from '@/lib/format'

export function FeedbackDetail() {
  const { t } = useTranslation('feedback')
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { data: report, isLoading, isError, error } = useFeedbackReport(id)

  const backLink = (
    <Link
      to="/feedback"
      className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      {t('detail.back')}
    </Link>
  )

  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy="true">
        {backLink}
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (isError || !report) {
    const notFound = error instanceof ApiError && error.status === 404
    return (
      <div>
        {backLink}
        {notFound ? (
          <EmptyState
            icon={SearchX}
            title={t('detail.notFound')}
            description={t('detail.notFoundDescription')}
          />
        ) : (
          <p className="py-8 text-center text-sm text-destructive">{t('list.loadError')}</p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        {backLink}
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{t(`types.${report.type}`)}</Badge>
          <FeedbackStatusBadge status={report.status} />
          {report.awaiting_reply && <AwaitingReplyBadge />}
        </div>
        <h1 className="mt-2 break-words text-3xl font-bold">{report.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('detail.sentBy', {
            name: report.author_name,
            date: formatDateTime(report.created_at),
          })}
        </p>
        {report.status_reason && (
          <p className="mt-1 text-sm">{t('detail.reason', { reason: report.status_reason })}</p>
        )}
      </div>

      <FeedbackTimeline status={report.status} />

      <Card>
        <CardHeader>
          <CardTitle>{t('detail.description')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="whitespace-pre-wrap break-words">{report.description}</p>
          <div className="text-sm">
            <span className="text-muted-foreground">{t('detail.page')}: </span>
            <span className="break-all">{report.page_url}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('detail.attachments')}</CardTitle>
        </CardHeader>
        <CardContent>
          {report.attachments.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {report.attachments.map((attachment) => (
                <AttachmentPreview key={attachment.id} reportId={report.id} attachment={attachment} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('detail.noAttachments')}</p>
          )}
        </CardContent>
      </Card>

      <FeedbackThread
        report={report}
        canReply={!!user && user.organization_id === report.organization_id}
      />

      {user?.is_platform_admin && <DiagnosticsPanel report={report} />}
    </div>
  )
}
